import { Check, Plus, XClose } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import {
  useCreateCreatorCohort,
  useCreatorCohortCounts,
  useCreatorCohorts,
  useSaveCreatorCohortAssignment,
} from "@/api/creator-cohorts";
import { Button } from "@/components/base/buttons/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { randomTagColor } from "@/lib/creator-tags/colors";
import { error, success, warning } from "@/providers/ToastProvider";

// How many missing-creator labels to spell out in the toast before collapsing
// the rest into "and N more".
const MAX_MISSING_SHOWN = 5;

// Sentinel for the "No cohort" (clear) option, distinct from "nothing selected".
const CLEAR = "__clear__";

export interface BulkCohortModalProps {
  creatorIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplied?: () => void;
  /** Resolve a creatorId to a human label (e.g. handle) for the "not found" toast. */
  getCreatorLabel?: (creatorId: string) => string | undefined;
}

// Single-select: pick one cohort to apply to the whole selection (replacing any
// existing cohort), or clear all of them. The baseline is the cohort the entire
// selection already shares (if any).
export const BulkCohortModal = ({
  creatorIds,
  open,
  onOpenChange,
  onApplied,
  getCreatorLabel,
}: BulkCohortModalProps) => {
  const { data: allCohorts = [] } = useCreatorCohorts();
  const { data: counts, isLoading: countsLoading } = useCreatorCohortCounts(creatorIds, open);
  const save = useSaveCreatorCohortAssignment();
  const createCohort = useCreateCreatorCohort();

  const [search, setSearch] = useState("");
  // `baseline` = the cohort the whole selection already shares (or null). `staged`
  // = the working choice the radios bind to (null = nothing chosen yet, CLEAR =
  // remove cohort). Save commits only when staged differs from baseline.
  const [baseline, setBaseline] = useState<string | null>(null);
  const [staged, setStaged] = useState<string | typeof CLEAR | null>(null);
  const seeded = useRef(false);

  // Seed once per open, once the counts have loaded. Reset everything on close.
  useEffect(() => {
    if (open && counts && !seeded.current) {
      const total = counts.total;
      // The shared cohort is the one assigned to every selected creator.
      const shared = total > 0 ? (Object.entries(counts.counts).find(([, n]) => n >= total)?.[0] ?? null) : null;
      setBaseline(shared);
      setStaged(shared);
      seeded.current = true;
    }
    if (!open) {
      seeded.current = false;
      setSearch("");
      setBaseline(null);
      setStaged(null);
    }
  }, [open, counts]);

  const query = search.trim();
  const filtered = allCohorts.filter((cohort) => cohort.name.toLowerCase().includes(query.toLowerCase()));
  // Offer "Create" only when the query doesn't exactly match an existing cohort.
  const hasExactMatch = allCohorts.some((cohort) => cohort.name.toLowerCase() === query.toLowerCase());
  const showCreate = query.length > 0 && !hasExactMatch;

  const handleCreate = async () => {
    if (!query) return;
    try {
      const cohort = await createCohort.mutateAsync({ name: query, color: randomTagColor() });
      setStaged(cohort.id);
      setSearch("");
    } catch {
      // toast handled in the hook
    }
  };

  const handleSave = async () => {
    if (staged === baseline || staged === null) {
      onOpenChange(false);
      return;
    }
    const cohortId = staged === CLEAR ? null : staged;

    try {
      const { creators, missingCreatorIds } = await save.mutateAsync({ creatorIds, cohortId });

      if (creators.length === 0) {
        error("Failed to apply cohort", {
          description: "None of the specified creators could be found.",
        });
        return;
      }

      const summary = `Updated cohort for ${creators.length} creator${creators.length === 1 ? "" : "s"}`;
      if (missingCreatorIds.length > 0) {
        warning(`${summary} — ${missingCreatorIds.length} could not be found`, {
          description: `Skipped: ${formatMissing(missingCreatorIds)}`,
        });
      } else {
        success(summary);
      }
      onApplied?.();
      onOpenChange(false);
    } catch {
      // A hard failure (e.g. a deleted cohort) is surfaced by the hook's error toast.
    }
  };

  // Build a readable "Skipped: …" list, mapping ids to labels where available
  // and collapsing the long tail.
  const formatMissing = (ids: string[]) => {
    const labels = ids.map((id) => getCreatorLabel?.(id) ?? "an unknown creator");
    const shown = labels.slice(0, MAX_MISSING_SHOWN);
    const extra = labels.length - shown.length;
    return extra > 0 ? `${shown.join(", ")} and ${extra} more` : shown.join(", ");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-[32rem]">
        <DialogHeader>
          <DialogTitle>
            Assign cohort to {creatorIds.length} selected creator{creatorIds.length === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>

        <Command shouldFilter={false} className="rounded-md border border-secondary">
          <CommandInput placeholder="Search or create a cohort..." value={search} onValueChange={setSearch} />
          <CommandList className="max-h-[50vh]">
            {countsLoading ? (
              <div className="py-8 text-center text-sm text-tertiary">Loading cohorts...</div>
            ) : (
              <>
                {!showCreate && filtered.length === 0 && <CommandEmpty>No cohorts found</CommandEmpty>}
                {showCreate && (
                  <CommandGroup>
                    <CommandItem value={`__create__${query}`} onSelect={handleCreate} className="gap-2">
                      <Plus className="size-4 shrink-0" />
                      <span className="truncate">
                        Create <span className="font-medium">“{query}”</span>
                      </span>
                    </CommandItem>
                  </CommandGroup>
                )}
                <CommandGroup>
                  <CommandItem value={CLEAR} onSelect={() => setStaged(CLEAR)} className="gap-2">
                    <XClose className="size-4 shrink-0 text-quaternary" />
                    <span className="truncate text-tertiary">No cohort</span>
                    {staged === CLEAR && <Check className="ml-auto size-4 shrink-0 text-brand" />}
                  </CommandItem>
                </CommandGroup>
                {filtered.length > 0 && (
                  <CommandGroup>
                    {filtered.map((cohort) => (
                      <CommandItem
                        key={cohort.id}
                        value={cohort.id}
                        onSelect={() => setStaged(cohort.id)}
                        className="gap-2"
                      >
                        <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: cohort.color }} />
                        <span className="truncate">{cohort.name}</span>
                        {staged === cohort.id && <Check className="ml-auto size-4 shrink-0 text-brand" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>

        <DialogFooter>
          <Button color="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button color="primary" isLoading={save.isPending} isDisabled={countsLoading} onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
